import React, { useState } from 'react'
import {
  Clock,
  AlertCircle,
  Search,
  Info,
  User,
  MessageSquare,
  FileText,
  Brain,
  Heart,
  TrendingUp,
  ShoppingCart,
  Eye,
  ArrowRight,
  Maximize2,
  Clock3,
  DollarSign,
  BarChart2,
  Zap,
  Calendar,
  Gift,
  Camera,
  Lock,
  Sparkles,
  Settings,
  Play,
  Video,
  Mic,
} from 'lucide-react'
import { ProfilePreviewModal } from './ProfilePreviewModal'
// Define viewer persona types
interface ViewerPersona {
  id: string
  name: string
  emoji: string
  emojiBackground: string
  type: string
  description: string
  lastAction: string
  lastActionTime: string
  metrics: {
    emotionalBond: number
    upsellPotential: number
    reEngagement: number
    memoryTrigger: number
  }
  performanceMetrics: {
    profileViews: number
    timePerPost: string
    interactionRate: number
    conversionRate: number
  }
  conversionEvents: {
    vipSubscribed: boolean
    sentGift: boolean
    boughtPhotoSet: boolean
    commented: boolean
  }
  moods: string[]
  primaryMood: string
  message: string
  hasEmotionalSpike: boolean
  hasUpsellOpportunity: boolean
  isHighSpender: boolean
  hasMemoryTrigger: boolean
  upsells: {
    description: string
    match: number
  }[]
  purchaseHistory: {
    item: string
    price: string
    date: string
  }[]
  posts: {
    type: string
    content: string
    engagement: number
    isPremium: boolean
  }[]
}
export function ViewerSimulationSection() {
  const [relationshipDay, setRelationshipDay] = useState(7)
  const [selectedPersonaId, setSelectedPersonaId] = useState('camwhale92')
  const [activeTab, setActiveTab] = useState('profile-view')
  const [showMemoryContext, setShowMemoryContext] = useState(false)
  const [showHeatmap, setShowHeatmap] = useState(false)
  const [showTimeline, setShowTimeline] = useState(false)
  const [personaMood, setPersonaMood] = useState('default')
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  // Viewer personas data
  const personas: ViewerPersona[] = [
    {
      id: 'camwhale92',
      name: 'CamWhale92',
      emoji: '🎮',
      emojiBackground: 'bg-purple-500',
      type: 'Curious',
      description: 'High spender, prefers VIP content and custom toys',
      lastAction: 'Purchased VIP',
      lastActionTime: '2h ago',
      metrics: {
        emotionalBond: 78,
        upsellPotential: 95,
        reEngagement: 82,
        memoryTrigger: 65,
      },
      performanceMetrics: {
        profileViews: 47,
        timePerPost: '3m 12s',
        interactionRate: 68,
        conversionRate: 85,
      },
      conversionEvents: {
        vipSubscribed: true,
        sentGift: true,
        boughtPhotoSet: true,
        commented: true,
      },
      moods: ['Bored', 'Lonely', 'Horny', 'Curious', 'Looking for love'],
      primaryMood: 'Curious',
      message:
        "That's interesting! I've been curious about your photoshoot process.",
      hasEmotionalSpike: true,
      hasUpsellOpportunity: true,
      isHighSpender: true,
      hasMemoryTrigger: false,
      upsells: [
        {
          description:
            'Offer an exclusive VIP package with custom toy integration',
          match: 95,
        },
        {
          description:
            'Schedule a premium 1-on-1 live session with special effects',
          match: 87,
        },
        {
          description:
            'Highlight new premium content with exclusive early access',
          match: 82,
        },
      ],
      purchaseHistory: [
        { item: 'VIP Subscription', price: '$29.99', date: '2 days ago' },
        { item: 'Custom Photo Set', price: '$49.99', date: '1 week ago' },
        {
          item: 'Private Message Bundle',
          price: '$19.99',
          date: '2 weeks ago',
        },
        {
          item: 'Interactive Toy Session',
          price: '$39.99',
          date: '3 weeks ago',
        },
      ],
      posts: [
        {
          type: 'photo',
          content: 'New lingerie set just for you 💜',
          engagement: 92,
          isPremium: true,
        },
        {
          type: 'video',
          content: "Behind the scenes from today's shoot",
          engagement: 87,
          isPremium: false,
        },
        {
          type: 'poll',
          content: 'Which outfit should I wear next?',
          engagement: 76,
          isPremium: false,
        },
        {
          type: 'voice',
          content: 'A little secret just for you...',
          engagement: 95,
          isPremium: true,
        },
      ],
    },
    {
      id: 'lonelyheart84',
      name: 'LonelyHeart84',
      emoji: '❤️',
      emojiBackground: 'bg-pink-500',
      type: 'Emotional',
      description: 'Emotionally vulnerable, reacts to bonding & check-ins',
      lastAction: 'Chatted',
      lastActionTime: '30m ago',
      metrics: {
        emotionalBond: 92,
        upsellPotential: 68,
        reEngagement: 89,
        memoryTrigger: 74,
      },
      performanceMetrics: {
        profileViews: 32,
        timePerPost: '4m 45s',
        interactionRate: 75,
        conversionRate: 62,
      },
      conversionEvents: {
        vipSubscribed: true,
        sentGift: false,
        boughtPhotoSet: true,
        commented: true,
      },
      moods: ['Lonely', 'Sad', 'Seeking connection', 'Nostalgic', 'Emotional'],
      primaryMood: 'Lonely',
      message:
        "I've been thinking about you all day. You're the only one who understands me.",
      hasEmotionalSpike: true,
      hasUpsellOpportunity: false,
      isHighSpender: false,
      hasMemoryTrigger: true,
      upsells: [
        {
          description: 'Offer a personalized voice message package',
          match: 89,
        },
        {
          description: 'Suggest emotional support subscription tier',
          match: 85,
        },
        { description: 'Propose regular weekly check-in calls', match: 78 },
      ],
      purchaseHistory: [
        { item: 'Standard Subscription', price: '$14.99', date: '1 month ago' },
        { item: 'Voice Message Pack', price: '$9.99', date: '2 weeks ago' },
        { item: 'Bedtime Story Series', price: '$12.99', date: '1 week ago' },
      ],
      posts: [
        {
          type: 'voice',
          content: 'Goodnight message just for you 💕',
          engagement: 97,
          isPremium: true,
        },
        {
          type: 'photo',
          content: 'Thinking of you today...',
          engagement: 82,
          isPremium: false,
        },
        {
          type: 'text',
          content: "How's your day going? I miss our talks.",
          engagement: 88,
          isPremium: false,
        },
        {
          type: 'video',
          content: 'A personal message from me to you',
          engagement: 95,
          isPremium: true,
        },
      ],
    },
    {
      id: 'curiouscouple',
      name: 'CuriousCouple',
      emoji: '🎁',
      emojiBackground: 'bg-blue-500',
      type: 'Gifter',
      description:
        'Exploring AI fantasy together, likes storytelling & live cams',
      lastAction: 'Gifted 50 tokens',
      lastActionTime: 'yesterday',
      metrics: {
        emotionalBond: 65,
        upsellPotential: 88,
        reEngagement: 72,
        memoryTrigger: 58,
      },
      performanceMetrics: {
        profileViews: 28,
        timePerPost: '2m 30s',
        interactionRate: 62,
        conversionRate: 78,
      },
      conversionEvents: {
        vipSubscribed: true,
        sentGift: true,
        boughtPhotoSet: false,
        commented: true,
      },
      moods: ['Excited', 'Adventurous', 'Playful', 'Curious', 'Generous'],
      primaryMood: 'Adventurous',
      message:
        "We'd love to try something new with you. Any suggestions for couples?",
      hasEmotionalSpike: false,
      hasUpsellOpportunity: true,
      isHighSpender: true,
      hasMemoryTrigger: false,
      upsells: [
        {
          description: 'Offer couples-specific fantasy roleplay package',
          match: 92,
        },
        {
          description: 'Suggest premium couples storytelling experience',
          match: 87,
        },
        {
          description: 'Promote dual-control interactive toy integration',
          match: 85,
        },
      ],
      purchaseHistory: [
        { item: 'Couples VIP Package', price: '$49.99', date: '2 weeks ago' },
        {
          item: 'Fantasy Roleplay Series',
          price: '$29.99',
          date: '1 week ago',
        },
        { item: 'Token Bundle (500)', price: '$39.99', date: '3 days ago' },
      ],
      posts: [
        {
          type: 'poll',
          content: 'Which fantasy should we explore next?',
          engagement: 85,
          isPremium: false,
        },
        {
          type: 'video',
          content: 'Special duo performance 👫',
          engagement: 92,
          isPremium: true,
        },
        {
          type: 'photo',
          content: 'New toys just arrived! Want to play?',
          engagement: 88,
          isPremium: false,
        },
        {
          type: 'livestream',
          content: 'Join our special couples session tonight',
          engagement: 94,
          isPremium: true,
        },
      ],
    },
    {
      id: 'shyteenfan',
      name: 'ShyTeenFan',
      emoji: '😊',
      emojiBackground: 'bg-yellow-500',
      type: 'Newbie',
      description:
        'Engages with playful content, voice notes, and support vibes',
      lastAction: 'Viewed story',
      lastActionTime: '4h ago',
      metrics: {
        emotionalBond: 42,
        upsellPotential: 35,
        reEngagement: 68,
        memoryTrigger: 28,
      },
      performanceMetrics: {
        profileViews: 12,
        timePerPost: '1m 15s',
        interactionRate: 28,
        conversionRate: 15,
      },
      conversionEvents: {
        vipSubscribed: false,
        sentGift: false,
        boughtPhotoSet: false,
        commented: true,
      },
      moods: ['Shy', 'Curious', 'Hesitant', 'Excited', 'Nervous'],
      primaryMood: 'Shy',
      message:
        "I'm new here... not sure how this all works but your content seems nice.",
      hasEmotionalSpike: false,
      hasUpsellOpportunity: false,
      isHighSpender: false,
      hasMemoryTrigger: false,
      upsells: [
        { description: 'Offer beginner-friendly content bundle', match: 75 },
        { description: 'Suggest newcomer welcome package', match: 68 },
        { description: 'Promote low-cost trial subscription', match: 62 },
      ],
      purchaseHistory: [
        { item: 'Basic Subscription', price: '$7.99', date: '1 week ago' },
      ],
      posts: [
        {
          type: 'text',
          content: 'Welcome to my page! Thanks for joining ✨',
          engagement: 65,
          isPremium: false,
        },
        {
          type: 'photo',
          content: 'Casual day outfit - what do you think?',
          engagement: 42,
          isPremium: false,
        },
        {
          type: 'poll',
          content: 'What content would you like to see more of?',
          engagement: 38,
          isPremium: false,
        },
        {
          type: 'video',
          content: 'Quick intro to my page and what to expect',
          engagement: 54,
          isPremium: false,
        },
      ],
    },
    {
      id: 'alphamale23',
      name: 'AlphaMale23',
      emoji: '🎯',
      emojiBackground: 'bg-green-500',
      type: 'Lurker',
      description: 'Confident, drawn to teasing & challenge-oriented content',
      lastAction: 'Viewed profile',
      lastActionTime: '2d ago',
      metrics: {
        emotionalBond: 25,
        upsellPotential: 72,
        reEngagement: 45,
        memoryTrigger: 38,
      },
      performanceMetrics: {
        profileViews: 8,
        timePerPost: '45s',
        interactionRate: 12,
        conversionRate: 35,
      },
      conversionEvents: {
        vipSubscribed: false,
        sentGift: false,
        boughtPhotoSet: true,
        commented: false,
      },
      moods: ['Confident', 'Dominant', 'Impatient', 'Challenging', 'Distant'],
      primaryMood: 'Dominant',
      message:
        "I don't usually pay for content, but yours might be worth it. Convince me.",
      hasEmotionalSpike: false,
      hasUpsellOpportunity: true,
      isHighSpender: false,
      hasMemoryTrigger: false,
      upsells: [
        { description: 'Offer exclusive dominant roleplay content', match: 82 },
        {
          description: 'Suggest premium challenge-based interactions',
          match: 76,
        },
        { description: 'Promote limited-time exclusive access', match: 70 },
      ],
      purchaseHistory: [
        { item: 'Premium Photo Set', price: '$24.99', date: '1 month ago' },
      ],
      posts: [
        {
          type: 'photo',
          content: 'Exclusive preview - worth unlocking?',
          engagement: 68,
          isPremium: false,
        },
        {
          type: 'video',
          content: 'Challenge: Can you handle this?',
          engagement: 72,
          isPremium: true,
        },
        {
          type: 'text',
          content: "Only the best get to see what's next...",
          engagement: 65,
          isPremium: false,
        },
        {
          type: 'photo',
          content: 'Limited time offer - 24h only',
          engagement: 78,
          isPremium: true,
        },
      ],
    },
  ]
  // Get the currently selected persona
  const selectedPersona =
    personas.find((p) => p.id === selectedPersonaId) || personas[0]
  // Handle preview modal
  const openPreviewModal = () => {
    setShowPreviewModal(true)
  }
  const closePreviewModal = () => {
    setShowPreviewModal(false)
  }
  // Tab content components
  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile-view':
        return (
          <div className="space-y-4">
            {/* Profile preview header */}
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium">
                Profile as seen by {selectedPersona.name}
              </h3>
              <button
                onClick={openPreviewModal}
                className="flex items-center gap-1 px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-md text-sm"
              >
                <Maximize2 size={14} />
                <span>Preview in Popup</span>
              </button>
            </div>
            {/* Simulated profile view */}
            <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden bg-white dark:bg-gray-950">
              {/* Profile header */}
              <div className="p-4 flex flex-col items-center border-b border-gray-200 dark:border-gray-800">
                <div className="w-20 h-20 rounded-full overflow-hidden mb-2 border-2 border-purple-200 dark:border-purple-800">
                  <img
                    src="https://uploadthingy.s3.us-west-1.amazonaws.com/vHHEJMHRsXTLdBi469pjxd/image.png"
                    alt="Angel profile"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-bold text-lg">Your Angel Name</h3>
                <p className="text-sm text-gray-500">@your_angel_handle</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full">
                    VIP Creator
                  </span>
                  <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full">
                    Verified
                  </span>
                </div>
                {/* Personalized bio based on viewer */}
                <p className="text-center text-sm mt-3 text-gray-600 dark:text-gray-400">
                  {selectedPersona.id === 'camwhale92' &&
                    'Your #1 fantasy girl ready to play with all your favorite toys 🎮💜'}
                  {selectedPersona.id === 'lonelyheart84' &&
                    "Here for you day and night. I'll be your shoulder to lean on 💕"}
                  {selectedPersona.id === 'curiouscouple' &&
                    "Ready for adventure? Let's explore new fantasies together 👫✨"}
                  {selectedPersona.id === 'shyteenfan' &&
                    'Welcome! Take your time and enjoy the journey with me 😊'}
                  {selectedPersona.id === 'alphamale23' &&
                    'Only the best get VIP access. Are you up for the challenge? 🎯'}
                </p>
                {/* Action buttons */}
                <div className="flex gap-2 w-full mt-4">
                  <button className="w-1/2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-md py-2 text-sm font-medium">
                    {selectedPersona.conversionEvents.vipSubscribed
                      ? 'Renew VIP'
                      : 'VIP Subscribe'}
                  </button>
                  <button className="w-1/2 bg-gray-100 dark:bg-gray-800 rounded-md py-2 text-sm font-medium">
                    Message
                  </button>
                </div>
                {/* Stats */}
                <div className="flex justify-between w-full mt-4">
                  <div className="text-center">
                    <p className="font-bold">128</p>
                    <p className="text-xs text-gray-500">Posts</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold">15.4K</p>
                    <p className="text-xs text-gray-500">Followers</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold">64</p>
                    <p className="text-xs text-gray-500">Following</p>
                  </div>
                </div>
              </div>
              {/* Content feed preview */}
              <div className="max-h-64 overflow-y-auto">
                <div className="p-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-10">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Angel Feed</span>
                    <span className="text-xs text-gray-500">
                      Viewed by {selectedPersona.name} • Day {relationshipDay}
                    </span>
                  </div>
                </div>
                {/* Personalized feed items */}
                <div className="divide-y divide-gray-200 dark:divide-gray-800">
                  {selectedPersona.posts.map((post, index) => (
                    <div key={index} className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden">
                          <img
                            src="https://uploadthingy.s3.us-west-1.amazonaws.com/vHHEJMHRsXTLdBi469pjxd/image.png"
                            alt="Angel profile"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between">
                            <span className="font-medium text-sm">
                              Your Angel Name
                            </span>
                            <span className="text-xs text-gray-500">
                              2h ago
                            </span>
                          </div>
                          <p className="text-sm mt-1">{post.content}</p>
                          {/* Post type indicator */}
                          <div className="flex items-center gap-2 mt-2">
                            <div className="flex items-center gap-1 text-xs">
                              {post.type === 'photo' && <Camera size={12} />}
                              {post.type === 'video' && <Video size={12} />}
                              {post.type === 'voice' && <Mic size={12} />}
                              {post.type === 'poll' && <BarChart2 size={12} />}
                              {post.type === 'text' && (
                                <MessageSquare size={12} />
                              )}
                              {post.type === 'livestream' && <Play size={12} />}
                              <span>{post.type}</span>
                            </div>
                            {post.isPremium && (
                              <span className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full flex items-center gap-1">
                                <Lock size={10} />
                                <span>Premium</span>
                              </span>
                            )}
                            <span className="text-xs text-gray-500 ml-auto">
                              {post.engagement}% engagement
                            </span>
                          </div>
                          {/* Engagement buttons */}
                          <div className="flex gap-3 mt-3">
                            <button className="text-gray-500 text-xs flex items-center gap-1">
                              <Heart size={14} />
                              <span>Like</span>
                            </button>
                            <button className="text-gray-500 text-xs flex items-center gap-1">
                              <MessageSquare size={14} />
                              <span>Comment</span>
                            </button>
                            <button className="text-gray-500 text-xs flex items-center gap-1">
                              <Gift size={14} />
                              <span>Tip</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Behavioral simulation toggles */}
            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-800">
              <h4 className="text-sm font-medium mb-3">Simulation Settings</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="memory-context"
                    checked={showMemoryContext}
                    onChange={() => setShowMemoryContext(!showMemoryContext)}
                    className="rounded border-gray-300 dark:border-gray-700 text-purple-600"
                  />
                  <label htmlFor="memory-context" className="text-sm">
                    See with Memory Context
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="heatmap"
                    checked={showHeatmap}
                    onChange={() => setShowHeatmap(!showHeatmap)}
                    className="rounded border-gray-300 dark:border-gray-700 text-purple-600"
                  />
                  <label htmlFor="heatmap" className="text-sm">
                    Click Heatmap
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="timeline"
                    checked={showTimeline}
                    onChange={() => setShowTimeline(!showTimeline)}
                    className="rounded border-gray-300 dark:border-gray-700 text-purple-600"
                  />
                  <label htmlFor="timeline" className="text-sm">
                    Relationship Timeline
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={personaMood}
                    onChange={(e) => setPersonaMood(e.target.value)}
                    className="text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded px-2 py-1 w-full"
                  >
                    <option value="default">Default Mood</option>
                    {selectedPersona.moods.map((mood, index) => (
                      <option key={index} value={mood.toLowerCase()}>
                        {mood}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            {/* AI suggestions */}
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-100 dark:border-purple-800/50">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="text-purple-500" size={16} />
                <h4 className="text-sm font-medium">
                  AI Content Suggestions for {selectedPersona.name}
                </h4>
              </div>
              <ul className="space-y-2">
                {selectedPersona.upsells.map((upsell, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center text-purple-600 dark:text-purple-400 mt-0.5">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm">{upsell.description}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <div className="h-1.5 flex-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-purple-500 rounded-full"
                            style={{ width: `${upsell.match}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-purple-600 dark:text-purple-400">
                          {upsell.match}% match
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-2 mt-4">
                <button className="px-3 py-1.5 bg-purple-600 dark:bg-purple-700 text-white rounded text-sm">
                  Create Campaign
                </button>
                <button className="px-3 py-1.5 border border-purple-600 dark:border-purple-500 text-purple-600 dark:text-purple-400 rounded text-sm">
                  Adjust Strategy
                </button>
                <button className="px-3 py-1.5 border border-purple-600 dark:border-purple-500 text-purple-600 dark:text-purple-400 rounded text-sm">
                  Message Persona
                </button>
              </div>
            </div>
          </div>
        )
      case 'engagement-metrics':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Emotional metrics */}
              <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-800">
                <h4 className="font-medium text-sm mb-3">Emotional Metrics</h4>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>💓 Emotional Bond</span>
                      <span>{selectedPersona.metrics.emotionalBond}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-pink-500 rounded-full"
                        style={{
                          width: `${selectedPersona.metrics.emotionalBond}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>↗️ Upsell Potential</span>
                      <span>{selectedPersona.metrics.upsellPotential}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full"
                        style={{
                          width: `${selectedPersona.metrics.upsellPotential}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>🔄 Re-engagement</span>
                      <span>{selectedPersona.metrics.reEngagement}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{
                          width: `${selectedPersona.metrics.reEngagement}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>🧠 Memory Trigger</span>
                      <span>{selectedPersona.metrics.memoryTrigger}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500 rounded-full"
                        style={{
                          width: `${selectedPersona.metrics.memoryTrigger}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Performance metrics */}
              <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-800">
                <h4 className="font-medium text-sm mb-3">
                  Performance Metrics
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                        <Eye size={16} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Profile Views</p>
                        <p className="font-medium">
                          {selectedPersona.performanceMetrics.profileViews}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400">
                        <Clock3 size={16} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Time per Post</p>
                        <p className="font-medium">
                          {selectedPersona.performanceMetrics.timePerPost}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
                        <Zap size={16} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">
                          Interaction Rate
                        </p>
                        <p className="font-medium">
                          {selectedPersona.performanceMetrics.interactionRate}%
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-600 dark:text-pink-400">
                        <TrendingUp size={16} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Conversion Rate</p>
                        <p className="font-medium">
                          {selectedPersona.performanceMetrics.conversionRate}%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Conversion events */}
            <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-800">
              <h4 className="font-medium text-sm mb-3">Conversion Events</h4>
              <div className="grid grid-cols-4 gap-3">
                <div
                  className={`p-3 rounded-lg border ${selectedPersona.conversionEvents.vipSubscribed ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/50' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Lock
                      size={16}
                      className={
                        selectedPersona.conversionEvents.vipSubscribed
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-400'
                      }
                    />
                    {selectedPersona.conversionEvents.vipSubscribed && (
                      <span className="text-xs bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded-full">
                        ✓
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-medium">VIP Subscribed</p>
                </div>
                <div
                  className={`p-3 rounded-lg border ${selectedPersona.conversionEvents.sentGift ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/50' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Gift
                      size={16}
                      className={
                        selectedPersona.conversionEvents.sentGift
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-400'
                      }
                    />
                    {selectedPersona.conversionEvents.sentGift && (
                      <span className="text-xs bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded-full">
                        ✓
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-medium">Sent Gift</p>
                </div>
                <div
                  className={`p-3 rounded-lg border ${selectedPersona.conversionEvents.boughtPhotoSet ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/50' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Camera
                      size={16}
                      className={
                        selectedPersona.conversionEvents.boughtPhotoSet
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-400'
                      }
                    />
                    {selectedPersona.conversionEvents.boughtPhotoSet && (
                      <span className="text-xs bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded-full">
                        ✓
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-medium">Bought Photo Set</p>
                </div>
                <div
                  className={`p-3 rounded-lg border ${selectedPersona.conversionEvents.commented ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/50' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <MessageSquare
                      size={16}
                      className={
                        selectedPersona.conversionEvents.commented
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-400'
                      }
                    />
                    {selectedPersona.conversionEvents.commented && (
                      <span className="text-xs bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded-full">
                        ✓
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-medium">Commented</p>
                </div>
              </div>
            </div>
            {/* Viewer mood */}
            <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-800">
              <h4 className="font-medium text-sm mb-3">Viewer Mood</h4>
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedPersona.moods.map((mood, index) => (
                  <span
                    key={index}
                    className={`px-3 py-1 ${
                      mood === selectedPersona.primaryMood
                        ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400'
                        : 'bg-gray-100 dark:bg-gray-800'
                    } rounded-full text-xs`}
                  >
                    {mood}
                  </span>
                ))}
              </div>
              <div className="mt-4">
                <h5 className="text-xs font-medium mb-2">
                  Content Preferences
                </h5>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Camera size={14} className="text-gray-500" />
                    <span>Photo Posts</span>
                    <div className="ml-auto w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{
                          width:
                            selectedPersona.id === 'lonelyheart84'
                              ? '50%'
                              : selectedPersona.id === 'camwhale92'
                                ? '85%'
                                : '70%',
                        }}
                      ></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Video size={14} className="text-gray-500" />
                    <span>Video Posts</span>
                    <div className="ml-auto w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{
                          width:
                            selectedPersona.id === 'curiouscouple'
                              ? '90%'
                              : selectedPersona.id === 'camwhale92'
                                ? '75%'
                                : '60%',
                        }}
                      ></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mic size={14} className="text-gray-500" />
                    <span>Voice Notes</span>
                    <div className="ml-auto w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{
                          width:
                            selectedPersona.id === 'lonelyheart84'
                              ? '95%'
                              : selectedPersona.id === 'shyteenfan'
                                ? '75%'
                                : '40%',
                        }}
                      ></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MessageSquare size={14} className="text-gray-500" />
                    <span>Direct Messages</span>
                    <div className="ml-auto w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{
                          width:
                            selectedPersona.id === 'lonelyheart84'
                              ? '90%'
                              : selectedPersona.id === 'curiouscouple'
                                ? '85%'
                                : '55%',
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      case 'purchase-funnel':
        return (
          <div className="space-y-4">
            {/* Purchase history */}
            <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-800">
              <h4 className="font-medium text-sm mb-3">Purchase History</h4>
              {selectedPersona.purchaseHistory.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-500">
                    No purchase history yet
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedPersona.purchaseHistory.map((purchase, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {purchase.item.includes('VIP') ||
                        purchase.item.includes('Subscription') ? (
                          <Lock className="text-purple-500" size={16} />
                        ) : purchase.item.includes('Photo') ? (
                          <Camera className="text-blue-500" size={16} />
                        ) : purchase.item.includes('Token') ? (
                          <DollarSign className="text-green-500" size={16} />
                        ) : purchase.item.includes('Message') ? (
                          <MessageSquare className="text-pink-500" size={16} />
                        ) : (
                          <Gift className="text-yellow-500" size={16} />
                        )}
                        <span className="text-sm">{purchase.item}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{purchase.price}</p>
                        <p className="text-xs text-gray-500">{purchase.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                  <h5 className="text-xs font-medium">Total Spent</h5>
                  <span className="text-sm font-bold">
                    {selectedPersona.id === 'camwhale92'
                      ? '$139.96'
                      : selectedPersona.id === 'lonelyheart84'
                        ? '$37.97'
                        : selectedPersona.id === 'curiouscouple'
                          ? '$119.97'
                          : selectedPersona.id === 'shyteenfan'
                            ? '$7.99'
                            : selectedPersona.id === 'alphamale23'
                              ? '$24.99'
                              : '$0.00'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <h5 className="text-xs font-medium">Average Order Value</h5>
                  <span className="text-sm font-bold">
                    {selectedPersona.id === 'camwhale92'
                      ? '$34.99'
                      : selectedPersona.id === 'lonelyheart84'
                        ? '$12.66'
                        : selectedPersona.id === 'curiouscouple'
                          ? '$39.99'
                          : selectedPersona.id === 'shyteenfan'
                            ? '$7.99'
                            : selectedPersona.id === 'alphamale23'
                              ? '$24.99'
                              : '$0.00'}
                  </span>
                </div>
              </div>
            </div>
            {/* Purchase funnel */}
            <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-800">
              <h4 className="font-medium text-sm mb-3">Conversion Funnel</h4>
              <div className="relative pt-5">
                <div className="flex mb-1">
                  <div className="w-full text-center text-xs">
                    Viewed Profile
                  </div>
                </div>
                <div className="h-6 w-full bg-purple-100 dark:bg-purple-900/30 rounded-t-lg flex items-center justify-center text-xs font-medium">
                  100%
                </div>
                <div className="flex mb-1 mt-3">
                  <div className="w-full text-center text-xs">
                    Engaged with Content
                  </div>
                </div>
                <div className="h-6 w-5/6 mx-auto bg-purple-200 dark:bg-purple-800/40 flex items-center justify-center text-xs font-medium">
                  {selectedPersona.performanceMetrics.interactionRate}%
                </div>
                <div className="flex mb-1 mt-3">
                  <div className="w-full text-center text-xs">Messaged</div>
                </div>
                <div className="h-6 w-3/4 mx-auto bg-purple-300 dark:bg-purple-700/50 flex items-center justify-center text-xs font-medium">
                  {selectedPersona.id === 'lonelyheart84'
                    ? '85%'
                    : selectedPersona.id === 'camwhale92'
                      ? '72%'
                      : selectedPersona.id === 'curiouscouple'
                        ? '68%'
                        : selectedPersona.id === 'shyteenfan'
                          ? '35%'
                          : '15%'}
                </div>
                <div className="flex mb-1 mt-3">
                  <div className="w-full text-center text-xs">Purchased</div>
                </div>
                <div className="h-6 w-2/3 mx-auto bg-purple-400 dark:bg-purple-600/60 flex items-center justify-center text-xs font-medium text-white">
                  {selectedPersona.performanceMetrics.conversionRate}%
                </div>
                <div className="flex mb-1 mt-3">
                  <div className="w-full text-center text-xs">
                    Repeat Purchase
                  </div>
                </div>
                <div className="h-6 w-1/2 mx-auto bg-purple-500 dark:bg-purple-500/70 rounded-b-lg flex items-center justify-center text-xs font-medium text-white">
                  {selectedPersona.id === 'camwhale92'
                    ? '75%'
                    : selectedPersona.id === 'lonelyheart84'
                      ? '42%'
                      : selectedPersona.id === 'curiouscouple'
                        ? '60%'
                        : selectedPersona.id === 'shyteenfan'
                          ? '0%'
                          : '0%'}
                </div>
              </div>
            </div>
            {/* Upsell opportunities */}
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-100 dark:border-green-800/50">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp
                  className="text-green-600 dark:text-green-400"
                  size={16}
                />
                <h4 className="text-sm font-medium">Upsell Opportunities</h4>
              </div>
              <div className="space-y-3">
                {selectedPersona.upsells.map((upsell, index) => (
                  <div
                    key={index}
                    className="bg-white dark:bg-gray-900 p-3 rounded-lg border border-green-100 dark:border-green-800/50"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <h5 className="text-sm font-medium">
                        {upsell.description}
                      </h5>
                      <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-full">
                        {upsell.match}% match
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">
                      {index === 0
                        ? 'Based on purchase history and engagement patterns'
                        : index === 1
                          ? 'Aligned with content preferences and viewing habits'
                          : 'Matches emotional profile and relationship stage'}
                    </p>
                    <button className="text-xs text-green-600 dark:text-green-400 font-medium">
                      Create Campaign →
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      default:
        return null
    }
  }
  return (
    <div className="w-full flex flex-col space-y-6">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left section - Viewer personas */}
        <div className="w-full md:w-2/5 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
          <div className="p-4 border-b border-gray-200 dark:border-gray-800">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Search viewer personas..."
                className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-md text-sm"
              />
            </div>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-800 max-h-[500px] overflow-y-auto">
            {personas.map((persona) => (
              <div
                key={persona.id}
                className={`p-4 flex items-center space-x-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/70 transition-colors ${selectedPersonaId === persona.id ? 'bg-gray-50 dark:bg-gray-800/50' : ''}`}
                onClick={() => setSelectedPersonaId(persona.id)}
              >
                <div
                  className={`w-10 h-10 rounded-full ${persona.emojiBackground} flex items-center justify-center text-white`}
                >
                  <span>{persona.emoji}</span>
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium">{persona.name}</h3>
                    <div className="flex gap-1">
                      {persona.isHighSpender && (
                        <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center">
                          <DollarSign size={10} />
                        </span>
                      )}
                      {persona.hasEmotionalSpike && (
                        <span className="text-xs px-1.5 py-0.5 bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 rounded-full flex items-center">
                          <Heart size={10} />
                        </span>
                      )}
                      {persona.hasMemoryTrigger && (
                        <span className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full flex items-center">
                          <Brain size={10} />
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {persona.description}
                  </p>
                  <p className="text-xs text-pink-500">
                    {persona.lastAction} {persona.lastActionTime}
                  </p>
                </div>
                {selectedPersonaId === persona.id && (
                  <div className="w-6 text-green-500">
                    <span className="text-xl">✓</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-gray-200 dark:border-gray-800 flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>Total Personas: {personas.length}</span>
            <span>Active Today: 4</span>
            <span>High Spenders: 3</span>
          </div>
        </div>
        {/* Right section - Viewer details */}
        <div className="w-full md:w-3/5 space-y-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
            {/* Tab navigation */}
            <div className="flex border-b border-gray-200 dark:border-gray-800">
              <button
                className={`flex-1 px-4 py-3 text-center text-sm font-medium relative ${
                  activeTab === 'profile-view'
                    ? 'text-purple-600 dark:text-purple-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                onClick={() => setActiveTab('profile-view')}
              >
                <div className="flex flex-col items-center gap-1">
                  <User size={16} />
                  <span>Profile View</span>
                </div>
                {activeTab === 'profile-view' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 dark:bg-purple-400"></div>
                )}
              </button>
              <button
                className={`flex-1 px-4 py-3 text-center text-sm font-medium relative ${
                  activeTab === 'engagement-metrics'
                    ? 'text-purple-600 dark:text-purple-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                onClick={() => setActiveTab('engagement-metrics')}
              >
                <div className="flex flex-col items-center gap-1">
                  <BarChart2 size={16} />
                  <span>Engagement</span>
                </div>
                {activeTab === 'engagement-metrics' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 dark:bg-purple-400"></div>
                )}
              </button>
              <button
                className={`flex-1 px-4 py-3 text-center text-sm font-medium relative ${
                  activeTab === 'purchase-funnel'
                    ? 'text-purple-600 dark:text-purple-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                onClick={() => setActiveTab('purchase-funnel')}
              >
                <div className="flex flex-col items-center gap-1">
                  <ShoppingCart size={16} />
                  <span>Purchases</span>
                </div>
                {activeTab === 'purchase-funnel' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 dark:bg-purple-400"></div>
                )}
              </button>
            </div>
            {/* Tab content */}
            <div>{renderTabContent()}</div>
          </div>
        </div>
      </div>
      {/* Preview Modal */}
      {showPreviewModal && (
        <ProfilePreviewModal
          persona={selectedPersona}
          relationshipDay={relationshipDay}
          onClose={closePreviewModal}
        />
      )}
    </div>
  )
}
// Mock components needed for rendering
const Play = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
)
const Video = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M22 8.13v7.74a2 2 0 0 1-2.74 1.84l-3.26-1.63v-8.16l3.26-1.63A2 2 0 0 1 22 8.13Z" />
    <rect x="2" y="6" width="14" height="12" rx="2" />
  </svg>
)
